/* CRYOTEIN cart drawer: subscription switches, 24-pack upgrades, 6 + 12-pack swap to a 24-pack subscription (combo),
 * switching back (undo), frequency, adds (one-off or with a plan), and a polite live region announcing each change.
 * Markup comes from snippets/cryo-*.liquid. Re-rendering uses Dawn's CartDrawer.renderContents.
 * Switching back (7 Oct 2026): every swap stores what it replaced in the hidden cart attribute _cryo_prev
 * ('<24-pack variant id>/<handle>|<variant id>|<qty>|<plan id or none>[,...]'), so the drawer's Liquid can draw the old
 * option as the unselected tile. Tapping that tile is the 'undo' action; it and line removal clear the attribute. */
(function () {
  'use strict';

  var SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  var PREV = '_cryo_prev';

  function toId(value) {
    if (value === undefined || value === null || value === '') return null;
    var n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  function withSections(body, sectionsUrl) {
    var copy = Object.assign({}, body);
    copy.sections = SECTIONS;
    copy.sections_url = sectionsUrl;
    return copy;
  }

  function finish(steps, sectionsUrl) {
    var last = steps[steps.length - 1];
    last.body = withSections(last.body, sectionsUrl);
    return steps;
  }

  function planRequests(action, data, sectionsUrl) {
    var quantity = toId(data.quantity) || 1;
    var steps;
    if (action === 'switch' || action === 'frequency') {
      steps = [{ path: 'cart/change.js', body: { id: data.lineKey, quantity: quantity, selling_plan: toId(data.planId) } }];
    } else if (action === 'upgrade' || action === 'combo' || action === 'undo') {
      // Add first, so a failure never leaves the cart emptier; then remove the replaced line(s) and record (or, for
      // undo, clear) what was replaced, in one update.
      var items = data.items !== undefined ? parseItems(data.items)
        : [{ id: toId(data.variantId), quantity: quantity, selling_plan: toId(data.planId) }];
      var updates = {};
      String(data.lineKeys || data.lineKey || '').split(',').forEach(function (key) { if (key) updates[key] = 0; });
      var attributes = {};
      attributes[PREV] = data.prevRecord || '';
      steps = [
        { path: 'cart/add.js', body: { items: items } },
        { path: 'cart/update.js', body: { updates: updates, attributes: attributes } },
      ];
    } else if (action === 'add') {
      var item = { id: toId(data.variantId), quantity: quantity };
      if (toId(data.planId) !== null) item.selling_plan = toId(data.planId);
      steps = [{ path: 'cart/add.js', body: { items: [item] } }];
    } else {
      throw new Error('Unknown cart action: ' + action);
    }
    return finish(steps, sectionsUrl);
  }

  // "id:qty[:plan],id:qty[:plan]" (data-items on the switch-back tile) to cart/add.js items.
  function parseItems(value) {
    return String(value || '').split(',').filter(Boolean).map(function (part) {
      var bits = part.split(':');
      var item = { id: toId(bits[0]), quantity: toId(bits[1]) || 1 };
      if (toId(bits[2]) !== null) item.selling_plan = toId(bits[2]);
      return item;
    });
  }

  // True when the cart still carries a _cryo_prev record but the 24-pack subscription it belongs to is gone
  // (removed with Dawn's own quantity controls), so the record must be cleared.
  function stalePrev(cart) {
    var record = cart && cart.attributes && cart.attributes[PREV];
    if (!record || !Array.isArray(cart.items)) return false;
    var target = toId(String(record).split('/')[0]);
    return !cart.items.some(function (it) { return it.variant_id === target && it.selling_plan_allocation; });
  }

  // Shopify cart errors carry a customer-readable `description` (e.g. "... is already sold out.").
  // Anything else (HTML error pages, empty bodies) returns null so the UI shows generic copy.
  function cartErrorMessage(bodyText) {
    try {
      var json = JSON.parse(bodyText);
      return (json && typeof json.description === 'string' && json.description) || null;
    } catch (e) {
      return null;
    }
  }

  // GET URL that re-renders the drawer sections for the page the customer is on. Used after a failed
  // multi-step action so the drawer shows the cart as it really is.
  function refreshUrl(pathname) {
    return (pathname || '/') + '?sections=' + SECTIONS.join(',');
  }

  // What the live region says after each action succeeds.
  function liveMessage(action) {
    switch (action) {
      case 'combo':
      case 'upgrade': return 'Switched to a 24-pack subscription. Switch back is available.';
      case 'switch': return 'Switched to subscription. Switch back is available.';
      case 'undo': return 'Switched back. Your cart is back as it was.';
      case 'frequency': return 'Delivery frequency updated.';
      case 'add': return 'Added to your cart.';
      default: return '';
    }
  }

  var api = {
    liveMessage: liveMessage,
    planRequests: planRequests,
    stalePrev: stalePrev,
    cartErrorMessage: cartErrorMessage,
    refreshUrl: refreshUrl,
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document === 'undefined') return;

  /* ---------- Browser runtime ---------- */

  var busy = false;

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function post(path, body) {
    return fetch(routeRoot() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.text().then(function (text) {
        if (!res.ok) {
          var error = new Error('Cart update failed with HTTP ' + res.status);
          error.customerMessage = cartErrorMessage(text);
          throw error;
        }
        return JSON.parse(text);
      });
    });
  }

  function runSteps(steps) {
    return steps.reduce(function (chain, step) {
      return chain.then(function () { return post(step.path, step.body); });
    }, Promise.resolve());
  }

  // A polite live region outside the drawer, so Dawn's re-render never replaces it mid-announcement.
  function announce(message) {
    if (!message) return;
    var region = document.getElementById('CryoCartLive');
    if (!region) {
      region = document.createElement('p');
      region.id = 'CryoCartLive';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    region.textContent = '';
    setTimeout(function () { region.textContent = message; }, 100);
  }

  function renderSections(state) {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && state && state.sections && typeof drawer.renderContents === 'function') {
      drawer.renderContents(state);
      drawer.classList.toggle('is-empty', !!drawer.querySelector('.drawer__inner-empty'));
    }
  }

  // Re-render the drawer from the server so it shows the cart as it really is.
  function refreshDrawer() {
    return fetch(refreshUrl(window.location.pathname), { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Drawer refresh failed with HTTP ' + res.status);
        return res.json();
      })
      .then(function (sections) { renderSections({ sections: sections }); });
  }

  // Focus after a re-render: the selected tile of the toggle (Subscribe after a swap, One-time after switching back),
  // else the drawer. Deferred so it runs after Dawn's own post-render open(), which is also a setTimeout.
  // After a tap or click (pointer) the focus moves without a visible ring; keyboard users keep the ring.
  function restoreFocus(pointer) {
    setTimeout(function () {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      var target = drawer.querySelector('.cryo-toggle [aria-checked="true"]') || drawer.querySelector('.drawer__inner');
      if (!target) return;
      try { target.focus({ focusVisible: !pointer }); } catch (e) { target.focus(); }
      if (typeof trapFocus === 'function') trapFocus(document.getElementById('CartDrawer'), target);
    });
  }

  // While a cryo request runs, every cryo button in the drawer is disabled so actions can't race.
  function lockActions(clicked) {
    busy = true;
    var buttons = document.querySelectorAll('cart-drawer [data-cryo-action]');
    for (var i = 0; i < buttons.length; i++) {
      if (!buttons[i].disabled) {
        buttons[i].disabled = true;
        buttons[i].setAttribute('data-cryo-locked', '');
      }
    }
    if (clicked) clicked.setAttribute('aria-busy', 'true');
  }

  function unlockActions() {
    busy = false;
    var buttons = document.querySelectorAll('[data-cryo-locked]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].disabled = false;
      buttons[i].removeAttribute('data-cryo-locked');
      buttons[i].removeAttribute('aria-busy');
    }
  }

  function errorContainer(el) {
    return el.closest('.cryo-bar, .cryo-empty, .cryo-line') || el.parentElement;
  }

  function showError(container, message) {
    if (!container) return;
    var el = container.querySelector('.cryo-error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'cryo-error';
      el.setAttribute('role', 'alert');
      container.appendChild(el);
    }
    el.textContent = message ? "Sorry, that didn't work: " + message : "Sorry, that didn't work. Please try again.";
  }

  // After a refresh the clicked button may be gone, so put the error on the toggle (or the drawer).
  function findErrorContainer(button) {
    if (button && button.isConnected) return errorContainer(button);
    return document.querySelector('cart-drawer .cryo-toggle') || document.getElementById('CartDrawer-CartErrors') ||
      document.querySelector('cart-drawer .drawer__inner');
  }

  // Runs the steps with every cryo button locked. Success: render, then restore focus.
  // Failure of a multi-step action: re-render the true cart first, then show the error.
  function perform(button, action, steps, pointer) {
    lockActions(button);
    return runSteps(steps)
      .then(function (state) {
        renderSections(state);
        unlockActions();
        announce(liveMessage(action));
        restoreFocus(pointer);
      })
      .catch(function (error) {
        var message = error && error.customerMessage;
        var refreshed = steps.length > 1 ? refreshDrawer().catch(function () { return null; }) : Promise.resolve();
        return refreshed.then(function () {
          unlockActions();
          showError(findErrorContainer(button), message);
        });
      });
  }

  document.addEventListener('click', function (event) {
    // Review card: "Read full review" opens the clamped text in place (cryo-cart-review.liquid).
    var more = event.target.closest('[data-cryo-review-toggle]');
    if (more) {
      var open = more.getAttribute('aria-expanded') !== 'true';
      var body = document.getElementById(more.getAttribute('aria-controls'));
      more.setAttribute('aria-expanded', String(open));
      more.textContent = open ? 'Show less' : 'Read full review';
      if (body) body.classList.toggle('is-open', open);
      return;
    }
    var button = event.target.closest('[data-cryo-action]');
    if (!button || busy || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    event.preventDefault();
    var action = button.getAttribute('data-cryo-action');
    var steps;
    try { steps = planRequests(action, button.dataset, window.location.pathname); } catch (e) { return; }
    // detail is 0 for a keyboard (Enter/Space) click, 1+ for a pointer click.
    perform(button, action, steps, event.detail > 0);
  });

  // A 24-pack subscription removed with Dawn's quantity controls leaves its _cryo_prev record behind: clear it, so a
  // later 24-pack subscription never shows someone else's "before". Dawn publishes cartUpdate with the cart.js state.
  function watchRemovals() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!stalePrev(event && event.cartData)) return;
      var attributes = {};
      attributes[PREV] = '';
      post('cart/update.js', { attributes: attributes }).catch(function () {});
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchRemovals);
  } else {
    watchRemovals();
  }
})();
